import { ClipboardList, Users } from "lucide-react"
import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils/helpers"
import { SectionCard } from "@/components/ui/SectionCard"
import { residentDetailPath, applicationDetailPath } from "../../utils/transferProcessPaths"
import { usesTransferWizardCardLayout } from "../../utils/transferPropertyCompanyWizard"
import { transferSelectionDisplayedCount } from "../../utils/transferSelectionDisplayCount"
import { TransferListingPaginationFooter } from "../TransferListingPaginationFooter"
import type { TransferResidentsTabProps } from "../../types"

const sharedTableScrollClass =
  "h-[min(28rem,75vh)] overflow-x-auto overflow-y-auto rounded-lg border border-slate-200"

function TransferResidentSection({
  useCardLayout,
  icon,
  title,
  testId,
  titleTestId,
  children,
}: {
  useCardLayout: boolean
  icon: LucideIcon
  title: string
  testId?: string
  titleTestId?: string
  children: ReactNode
}) {
  if (!useCardLayout) return <>{children}</>
  return (
    <SectionCard icon={icon} title={title} testId={testId} titleTestId={titleTestId}>
      {children}
    </SectionCard>
  )
}

export function TransferResidentsTab({ ctx }: TransferResidentsTabProps) {
  /* eslint-disable react-hooks/refs -- pagination params live on mutable refs (Angular parity) */
  const {
    hasPolicy,
    fragment,
    selectedTab,
    residents,
    applications,
    residentTablesRevision,
    pageOptions,
    pageOptionsApplication,
    urlParamsRef,
    urlParamsAppRef,
    selectedResidentList,
    setSelectedResidentList,
    selectedApplicationList,
    setSelectedApplicationList,
    loadResidents,
    loadApplications,
    selectAllResident,
    selectAllApplication,
  } = ctx
  if (!fragment) return null

  const useCardLayout = usesTransferWizardCardLayout(fragment)
  const showTransferSelection = ["transfer_resident", "split_property"].includes(fragment)
  const residentsPanelGridClass = useCardLayout
    ? "grid min-w-0 gap-4 lg:grid-cols-2 lg:items-start animate-in fade-in duration-300"
    : "grid min-w-0 gap-6 md:grid-cols-2 md:items-start"

  return (
      <div
        data-testid="transfer-process-residents-tab-panel"
        className={cn(
          residentsPanelGridClass,
          selectedTab !== "residentinfo" && "hidden",
        )}
        hidden={selectedTab !== "residentinfo"}
        aria-hidden={selectedTab !== "residentinfo"}
      >
          <TransferResidentSection
            useCardLayout={useCardLayout}
            icon={Users}
            title="Residents List"
            testId="transfer-process-residents-list-section"
            titleTestId="transfer-process-residents-list-heading"
          >
          <div className="min-w-0">
            {!useCardLayout && (
            <h3
              data-testid="transfer-process-residents-list-heading"
              className="mb-2 text-sm font-semibold"
            >
              Residents List
            </h3>
            )}
            <div
              className={
                useCardLayout
                  ? "min-w-0"
                  : "rounded-xl border border-slate-200 bg-white shadow-sm"
              }
            >
              <div
                data-testid="transfer-process-residents-table-container"
                key={`residents-table-${residentTablesRevision}`}
                className={
                  useCardLayout
                    ? sharedTableScrollClass
                    : "h-[min(28rem,75vh)] overflow-x-auto overflow-y-auto"
                }
              >
              <table
                data-testid="transfer-process-residents-table"
                className="min-w-[640px] w-full text-left text-xs text-slate-700 sm:min-w-full"
              >
                <thead
                  data-testid="transfer-process-residents-table-thead"
                  className="sticky top-0 z-[1] bg-slate-100"
                >
                  <tr data-testid="transfer-process-residents-table-header-row">
                    {showTransferSelection && (
                      <th
                        data-testid="transfer-process-residents-col-transfer"
                        className="p-2"
                      >
                        <label
                          data-testid="transfer-process-residents-select-all-label"
                          className="flex items-center gap-1"
                        >
                          <input
                            type="checkbox"
                            data-testid="transfer-process-residents-select-all-checkbox"
                            checked={
                              residents.length > 0 &&
                              selectedResidentList.length === residents.length &&
                              residents.every((row) => selectedResidentList.includes(row.application_id))
                            }
                            onChange={(e) => selectAllResident(e.target.checked)}
                          />
                          Transfer
                        </label>
                      </th>
                    )}
                    <th data-testid="transfer-process-residents-col-applicant" className="p-2">
                      Applicant
                    </th>
                    <th data-testid="transfer-process-residents-col-resident-name" className="p-2">
                      Resident Name
                    </th>
                    <th data-testid="transfer-process-residents-col-email" className="p-2">
                      Email
                    </th>
                    <th data-testid="transfer-process-residents-col-company" className="p-2">
                      Company
                    </th>
                    <th data-testid="transfer-process-residents-col-property" className="p-2">
                      Property
                    </th>
                    <th data-testid="transfer-process-residents-col-lease-start" className="p-2">
                      Lease Start Date
                    </th>
                    <th data-testid="transfer-process-residents-col-rental-status" className="p-2">
                      Rental Status
                    </th>
                    <th data-testid="transfer-process-residents-col-rental-progress" className="p-2">
                      Rental Progress
                    </th>
                    <th data-testid="transfer-process-residents-col-action" className="p-2">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {residents.length === 0 ? (
                    <tr data-testid="transfer-process-residents-table-empty-row">
                      <td
                        colSpan={showTransferSelection ? 10 : 9}
                        className="px-4 py-10 text-center text-sm text-slate-500"
                      >
                        No residents found.
                      </td>
                    </tr>
                  ) : (
                  residents.map((r) => {
                    const residentDetailTo = residentDetailPath(r)
                    return (
                      <tr
                        key={r.application_id}
                        data-testid={`transfer-process-residents-row-${r.application_id}`}
                        className="border-t border-slate-100"
                      >
                        {showTransferSelection && (
                          <td className="p-2">
                            <input
                              type="checkbox"
                              data-testid={`transfer-process-residents-row-checkbox-${r.application_id}`}
                              checked={selectedResidentList.includes(r.application_id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedResidentList((x) => [...x, r.application_id])
                                else setSelectedResidentList((x) => x.filter((id) => id !== r.application_id))
                              }}
                            />
                          </td>
                        )}
                        <td className="p-2">{String(r.applicant_id ?? "").padStart(8, "0")}</td>
                        <td className="p-2">
                          {r.first_name} {r.last_name}
                        </td>
                        <td className="p-2">{r.email}</td>
                        <td className="p-2">{r.company_name ?? ""}</td>
                        <td className="p-2">{r.property_name ?? ""}</td>
                        <td className="p-2">{r.contract_start_date ?? ""}</td>
                        <td className="p-2">{r.rental_status ?? "NOT_STARTED"}</td>
                        <td className="p-2">{r.rental_progress ?? ""}</td>
                        <td className="p-2">
                          {hasPolicy(["resident_detail_view"]) ? (
                            residentDetailTo ? (
                              <Link
                                className="text-[rgb(21_151_255)] underline hover:text-[#0d7acc]"
                                data-testid={`transfer-process-residents-edit-link-${r.applicant_id}`}
                                to={residentDetailTo}
                              >
                                Edit
                              </Link>
                            ) : (
                              <span
                                data-testid={`transfer-process-residents-edit-disabled-${r.applicant_id}`}
                                className="text-slate-400 cursor-not-allowed"
                                title="Resident user id unavailable"
                              >
                                Edit
                              </span>
                            )
                          ) : (
                            <span
                              data-testid={`transfer-process-residents-edit-disabled-${r.applicant_id}`}
                              className="text-slate-400 cursor-not-allowed"
                            >
                              Edit
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                  )}
                </tbody>
              </table>
              </div>
            <TransferListingPaginationFooter
              pageOptions={pageOptions}
              perPage={urlParamsRef.current.perPage}
              totalSelectedDisplay={transferSelectionDisplayedCount(
                selectedResidentList,
                residents,
                pageOptions.total,
              )}
              onPageChange={(p) => {
                urlParamsRef.current.page = p
                void loadResidents({ silent: true })
              }}
              onPerPageChange={(n) => {
                urlParamsRef.current.perPage = n
                urlParamsRef.current.page = 1
                void loadResidents({ silent: true })
              }}
              testIdPrefix="transfer-process-residents"
              containerDataTestId="transfer-process-residents-pagination"
              perPageSelectDataTestId="transfer-process-residents-per-page-select"
            />
            </div>
          </div>
          </TransferResidentSection>

          <TransferResidentSection
            useCardLayout={useCardLayout}
            icon={ClipboardList}
            title="Pending Applications"
            testId="transfer-process-applications-list-section"
            titleTestId="transfer-process-applications-list-heading"
          >
          <div className="min-w-0">
            {!useCardLayout && (
            <h3
              data-testid="transfer-process-applications-list-heading"
              className="mb-2 text-sm font-semibold"
            >
              Pending Applications
            </h3>
            )}
            <div
              className={
                useCardLayout
                  ? "min-w-0"
                  : "rounded-xl border border-slate-200 bg-white shadow-sm"
              }
            >
              <div
                data-testid="transfer-process-applications-table-container"
                key={`applications-table-${residentTablesRevision}`}
                className={
                  useCardLayout
                    ? sharedTableScrollClass
                    : "h-[min(28rem,75vh)] overflow-x-auto overflow-y-auto"
                }
              >
              <table
                data-testid="transfer-process-applications-table"
                className="min-w-[560px] w-full text-left text-xs text-slate-700 sm:min-w-full"
              >
                <thead
                  data-testid="transfer-process-applications-table-thead"
                  className="sticky top-0 z-[1] bg-slate-100"
                >
                  <tr data-testid="transfer-process-applications-table-header-row">
                    {showTransferSelection && (
                      <th
                        data-testid="transfer-process-applications-col-transfer"
                        className="p-2"
                      >
                        <label
                          data-testid="transfer-process-applications-select-all-label"
                          className="flex items-center gap-1"
                        >
                          <input
                            type="checkbox"
                            data-testid="transfer-process-applications-select-all-checkbox"
                            checked={
                              applications.length > 0 &&
                              selectedApplicationList.length === applications.length &&
                              applications.every((row) => selectedApplicationList.includes(row.application_id))
                            }
                            onChange={(e) => selectAllApplication(e.target.checked)}
                          />
                          Transfer
                        </label>
                      </th>
                    )}
                    <th data-testid="transfer-process-applications-col-applicant" className="p-2">
                      Applicant
                    </th>
                    <th data-testid="transfer-process-applications-col-resident-name" className="p-2">
                      Resident Name
                    </th>
                    <th data-testid="transfer-process-applications-col-email" className="p-2">
                      Email
                    </th>
                    <th data-testid="transfer-process-applications-col-company" className="p-2">
                      Company
                    </th>
                    <th data-testid="transfer-process-applications-col-property" className="p-2">
                      Property
                    </th>
                    <th data-testid="transfer-process-applications-col-status" className="p-2">
                      Status
                    </th>
                    <th data-testid="transfer-process-applications-col-action" className="p-2">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {applications.length === 0 ? (
                    <tr data-testid="transfer-process-applications-table-empty-row">
                      <td
                        colSpan={showTransferSelection ? 8 : 7}
                        className="px-4 py-10 text-center text-sm text-slate-500"
                      >
                        No pending applications found.
                      </td>
                    </tr>
                  ) : (
                  applications.map((r) => {
                    const pendingAppDetailTo = applicationDetailPath(r)
                    return (
                      <tr
                        key={r.application_id}
                        data-testid={`transfer-process-applications-row-${r.application_id}`}
                        className="border-t border-slate-100"
                      >
                        {showTransferSelection && (
                          <td className="p-2">
                            <input
                              type="checkbox"
                              data-testid={`transfer-process-applications-row-checkbox-${r.application_id}`}
                              checked={selectedApplicationList.includes(r.application_id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedApplicationList((x) => [...x, r.application_id])
                                else setSelectedApplicationList((x) => x.filter((id) => id !== r.application_id))
                              }}
                            />
                          </td>
                        )}
                        <td className="p-2">{String(r.applicant_id ?? "").padStart(8, "0")}</td>
                        <td className="p-2">
                          {r.first_name} {r.last_name}
                        </td>
                        <td className="p-2">{r.email}</td>
                        <td className="p-2">{r.company_name ?? ""}</td>
                        <td className="p-2">{r.property_name ?? ""}</td>
                        <td className="p-2">{r.applicant_status}</td>
                        <td className="p-2">
                          {hasPolicy(["application_detail_view"]) ? (
                            pendingAppDetailTo ? (
                              <Link
                                className="text-[rgb(21_151_255)] underline hover:text-[#0d7acc]"
                                data-testid={`transfer-process-applications-edit-link-${r.applicant_id}`}
                                to={pendingAppDetailTo}
                              >
                                Edit
                              </Link>
                            ) : (
                              <span
                                data-testid={`transfer-process-applications-edit-disabled-${r.applicant_id}`}
                                className="text-slate-400 cursor-not-allowed"
                                title="Applicant id unavailable"
                              >
                                Edit
                              </span>
                            )
                          ) : (
                            <span
                              data-testid={`transfer-process-applications-edit-disabled-${r.applicant_id}`}
                              className="text-slate-400 cursor-not-allowed"
                            >
                              Edit
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                  )}
                </tbody>
              </table>
              </div>
            <TransferListingPaginationFooter
              pageOptions={pageOptionsApplication}
              perPage={urlParamsAppRef.current.perPage}
              totalSelectedDisplay={transferSelectionDisplayedCount(
                selectedApplicationList,
                applications,
                pageOptionsApplication.total,
              )}
              onPageChange={(p) => {
                urlParamsAppRef.current.page = p
                void loadApplications({ silent: true })
              }}
              onPerPageChange={(n) => {
                urlParamsAppRef.current.perPage = n
                urlParamsAppRef.current.page = 1
                void loadApplications({ silent: true })
              }}
              testIdPrefix="transfer-process-applications"
              containerDataTestId="transfer-process-applications-pagination"
              perPageSelectDataTestId="transfer-process-applications-per-page-select"
            />
            </div>
          </div>
          </TransferResidentSection>
        </div>
  )
}
